import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AiKnowledgeService } from '../../../ai-knowledge.service';
import { AiKnowledgeI18nModule } from '../../../assets/i18n/ai-knowledge-i18n.module';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { KnowledgeSource, TrainingProgress, WebsiteScrapeConfigProgress } from '@vendasta/embeddings';
import { defer, from, map, retry, Subject, take, takeUntil } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KnowledgeSourceWithSuggestedFlag } from '../../../ai-knowledge-util';
import { MatDialog } from '@angular/material/dialog';
import { RescrapePagesComponent } from '../rescrape-pages/rescrape-pages.component';

@Component({
  selector: 'ai-knowledge-last-scraped',
  imports: [CommonModule, TranslateModule, AiKnowledgeI18nModule],
  providers: [AiKnowledgeService],
  templateUrl: './last-scraped.component.html',
  styleUrls: ['./last-scraped.component.scss'],
})
export class LastScrapedComponent implements OnChanges {
  @Input() knowledgeSource: KnowledgeSourceWithSuggestedFlag;
  @Input() allowRescrapeWebsite: boolean;
  @Input() hasUnsavedChanges: boolean;
  @Output() rescrapeInitiated = new EventEmitter<KnowledgeSource>();
  @Output() refreshSource = new EventEmitter<void>();

  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stopPolling$$: Subject<void> = new Subject();
  private readonly dialog = inject(MatDialog);

  WebsiteScrapeConfigProgress = WebsiteScrapeConfigProgress;
  TrainingProgress = TrainingProgress;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['knowledgeSource'] && this.knowledgeSourceInProgress() && !this.knowledgeSource?.isSuggested) {
      this.pollUntilWebsiteProgressIsDone();
    }
  }

  getLastScraped(): string {
    return this.knowledgeService.getLastScraped(this.knowledgeSource);
  }

  getTrainedPagesCount(): number {
    return (
      this.knowledgeSource?.config?.websiteScrapeConfig?.scrapedPages?.filter(
        (p) => p.trainingEnabled && !p.errorMessage,
      )?.length || 0
    );
  }

  getScrapedPagesCount(): number {
    return this.knowledgeSource?.config?.websiteScrapeConfig?.scrapedPages?.filter((p) => !p.errorMessage)?.length || 0;
  }

  async rescrape(): Promise<void> {
    this.dialog
      .open(RescrapePagesComponent, {
        data: { knowledgeSource: this.knowledgeSource },
      })
      .afterClosed()
      .subscribe(async (refreshed: boolean) => {
        if (refreshed) {
          this.rescrapeInitiated.emit(this.knowledgeSource);
          this.pollUntilWebsiteProgressIsDone();
        } else {
          this.refreshSource.emit();
        }
      });
  }

  pollUntilWebsiteProgressIsDone(): void {
    // stop previous polling
    this.stopPolling$$.next();

    defer(() =>
      from(this.knowledgeService.getKnowledgeSource(this.knowledgeSource?.id, this.knowledgeSource?.namespace)),
    )
      .pipe(
        map((ks) => {
          this.knowledgeSource = ks;
          if (this.knowledgeSourceInProgress()) {
            // throw error to poll again
            throw false;
          }
        }),
        retry({ delay: 5000 }),
        take(1),
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.stopPolling$$),
      )
      .subscribe();
  }

  knowledgeSourceInProgress(): boolean {
    return (
      this.knowledgeSource?.config?.websiteScrapeConfig &&
      (this.knowledgeSource?.config?.websiteScrapeConfig?.scrapeProgress !==
        WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE ||
        this.knowledgeSource?.training?.progress !== TrainingProgress.TRAINING_DONE)
    );
  }
}
